/**
 * TestsPanel — per-class coverage + run/improve actions for the multi-agent run.
 *
 * COPIED from pages/dev-agent/components/rightPanels/TestsPanel.tsx with:
 *   - useDevAgentStore                      -> useMultiAgentIdeStore
 *   - feed-derived `testItems: FeedItem[]`  -> store `testRuns` via deriveTestPanelRows
 *     (multi-agent has no feedItems; rows come from the typed testRuns slice)
 *   - dev-agent getMetadataItemByName       -> local resolver (resolveDependencyItemForEditor)
 *   - runTest / coverage no longer need a sessionUuid (runTest reads runId from
 *     useMultiAgentStore); the multi-class "Improve" clarification (which used the
 *     dev-agent feed's addFeedItem) sends the lowest-coverage class to chat instead.
 * Colours / layout are byte-identical to the dev-agent panel.
 */
import { useEffect, useState } from "react";
import { TestTube2 } from "lucide-react";
import toast from "react-hot-toast";
import { useMultiAgentIdeStore } from "@/pages/multi-agent/ide/store/multiAgentIdeStore";
import type { CoveredClass } from "@/pages/multi-agent/ide/store/multiAgentIdeStore";
import { PanelSection } from "./PanelSection";
import { deriveTestPanelRows, type TestPanelRow } from "./derivePanelData";
import { getMetadataItemByName } from "./resolveDependencyItemForEditor";

function preferredCoverageTarget(
  data: TestPanelRow["data"],
  coveredClasses: CoveredClass[],
) {
  const preferredName = data.productionClass;
  if (preferredName) {
    const exact = coveredClasses.find((coveredClass) => coveredClass.name === preferredName);
    if (exact) return exact;
  }
  return coveredClasses[0] || null;
}

export function TestsPanel({
  analyzingFile,
  isBusy,
  onMoveUp,
  onMoveDown,
  onSendMessage,
  onOpenFileInEditor,
}: {
  analyzingFile?: string | null;
  isBusy?: boolean;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  onSendMessage: (msg: string, displayText?: string) => void;
  onOpenFileInEditor: (file: { id: string; name: string; metadataType?: string }) => void;
}) {
  const runningTest = useMultiAgentIdeStore((state) => state.runningTest);
  const testRuns = useMultiAgentIdeStore((state) => state.testRuns);
  const clearTestRuns = useMultiAgentIdeStore((state) => state.clearTestRuns);
  // Aggregate coverage from the post-save analyze chain (MA-9: aggregate stays
  // in TestsPanel). Newest first.
  const testCoverageData = useMultiAgentIdeStore((state) => state.testCoverageData);
  const aggregateRows = [...testCoverageData].reverse();
  const [expandedTestRow, setExpandedTestRow] = useState<string | null>(null);
  const [runElapsedSec, setRunElapsedSec] = useState(0);

  useEffect(() => {
    if (!runningTest) {
      setRunElapsedSec(0);
      return;
    }
    setRunElapsedSec(Math.round((Date.now() - runningTest.startedAt) / 1000));
    const timer = setInterval(() => {
      setRunElapsedSec(Math.round((Date.now() - runningTest.startedAt) / 1000));
    }, 1000);
    return () => clearInterval(timer);
  }, [runningTest]);

  const rows = deriveTestPanelRows(testRuns);

  return (
    <PanelSection
      disabled={isBusy}
      title="Test Classes"
      icon={<TestTube2 className="w-3.5 h-3.5" />}
      count={testRuns.length + aggregateRows.length}
      onClear={testRuns.length > 0 ? clearTestRuns : undefined}
      onMoveUp={onMoveUp}
      onMoveDown={onMoveDown}
    >
      {aggregateRows.length > 0 && (
        <div className="mb-1 space-y-1">
          {aggregateRows.map((entry) => {
            const pct = entry.aggregate_coverage_pct ?? null;
            return (
              <div
                key={`agg-${entry.class_name}`}
                className="py-1.5 px-2 rounded bg-muted/20 border border-border/40"
              >
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-xs text-foreground truncate">
                    {entry.class_name}
                  </span>
                  <span className="text-[9px] font-medium uppercase tracking-wide text-muted-foreground">
                    aggregate
                  </span>
                </div>
                {pct != null && (
                  <div className="flex items-center gap-2 mt-0.5">
                    <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
                      <div
                        className={`h-full rounded-full ${pct >= 75 ? "bg-ma-ok" : pct >= 50 ? "bg-ma-warn" : "bg-ma-err"}`}
                        style={{ width: `${Math.min(pct, 100)}%` }}
                      />
                    </div>
                    <span
                      className={`text-[11px] font-semibold ${pct >= 75 ? "text-ma-ok-text" : pct >= 50 ? "text-ma-warn-text" : "text-ma-err-text"}`}
                    >
                      {pct}%
                    </span>
                  </div>
                )}
                {entry.test_classes && entry.test_classes.length > 0 && (
                  <div className="text-[11px] text-muted-foreground mt-0.5">
                    {entry.test_classes.length} test class{entry.test_classes.length === 1 ? "" : "es"}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {runningTest && (
        <div className="flex items-center gap-2 px-2 py-1.5 mb-1 rounded bg-muted/30 border border-border/40">
          <div className="h-3 w-3 animate-spin rounded-full border-[1.5px] border-primary border-t-transparent" />
          <span className="text-xs text-foreground truncate flex-1">
            Running {runningTest.testClassName}… ({runElapsedSec}s)
          </span>
          <button
            type="button"
            onClick={() => useMultiAgentIdeStore.getState().stopTest()}
            className="text-[11px] text-ma-err-text hover:underline shrink-0"
          >
            Stop
          </button>
        </div>
      )}
      {analyzingFile && rows.length === 0 ? (
        <div className="flex items-center gap-2 justify-center py-2 text-muted-foreground">
          <div className="h-3 w-3 animate-spin rounded-full border-[1.5px] border-primary border-t-transparent" />
          <span className="text-xs font-medium">Running tests for {analyzingFile}...</span>
        </div>
      ) : rows.length > 0 ? (
        <div className="space-y-1">
          {rows.map((item) => {
            const data = item.data;
            const coverage = data.coveragePct ?? data.coverage;
            const coveredClasses: CoveredClass[] = Array.isArray(data.coveredClasses)
              ? data.coveredClasses
              : [];
            const primaryCoverageTarget = preferredCoverageTarget(data, coveredClasses);
            const isExpanded = expandedTestRow === item.id;
            return (
              <div
                key={item.id}
                className="w-full text-left py-1.5 px-2 rounded transition-colors group hover:bg-muted/50 cursor-pointer"
                onClick={() => setExpandedTestRow((current) => (current === item.id ? null : item.id))}
              >
                {data.className && (
                  <div className="font-semibold text-xs text-foreground">{data.className}</div>
                )}
                {coverage != null && (
                  <>
                    <div className="flex items-center gap-2 mt-0.5">
                      <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
                        <div
                          className={`h-full rounded-full ${coverage >= 75 ? "bg-ma-ok" : coverage >= 50 ? "bg-ma-warn" : "bg-ma-err"}`}
                          style={{ width: `${Math.min(coverage, 100)}%` }}
                        />
                      </div>
                      <span
                        className={`text-[11px] font-semibold ${coverage >= 75 ? "text-ma-ok-text" : coverage >= 50 ? "text-ma-warn-text" : "text-ma-err-text"}`}
                      >
                        {coverage}%
                      </span>
                    </div>
                    {data.uncoveredCount != null && (
                      <div className="text-[11px] text-muted-foreground mt-0.5">
                        {coverage}% covered, {data.uncoveredCount} uncovered lines
                      </div>
                    )}
                  </>
                )}
                {data.status && (
                  <span
                    className={`text-[11px] font-semibold ml-1 ${data.status === "passed" ? "text-ma-ok-text" : data.status === "failed" ? "text-ma-err-text" : "text-muted-foreground"}`}
                  >
                    {data.status}
                  </span>
                )}
                <div className="flex flex-wrap items-center gap-x-1 gap-y-0.5 mt-1.5">
                  <button
                    type="button"
                    onClick={async (event) => {
                      event.stopPropagation();
                      const target = primaryCoverageTarget;
                      if (!target) {
                        toast.error("Sorry, no coverage data — re-run the test first");
                        return;
                      }
                      try {
                        const orgId = (await import("@/stores/authStore_v1")).useAuthStoreV1.getState().selectedOrg?.id;
                        let fileId = target.name;
                        try {
                          const item = await getMetadataItemByName(target.name, orgId, true);
                          if (item?.id) fileId = String(item.id);
                        } catch {
                          /* fall through to name-based open */
                        }
                        onOpenFileInEditor({
                          id: fileId,
                          name: target.name,
                          metadataType: "ApexClass",
                        });
                        useMultiAgentIdeStore.getState().setCoverageLines(
                          target.name,
                          target.covered_lines || [],
                          target.uncovered_lines || [],
                          target.coverage_pct,
                        );
                        toast.success(`${target.name}: ${target.coverage_pct ?? 0}%`);
                      } catch {
                        toast.error(`Sorry, couldn't open ${target.name}`);
                      }
                    }}
                    className="text-[11px] font-medium text-ma-info-text hover:underline hover:underline"
                  >
                    Coverage
                  </button>
                  <span className="text-[11px] text-muted-foreground select-none" aria-hidden>
                    {"·"}
                  </span>
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      const testClassName = data.className;
                      if (!testClassName) return;
                      if (!coveredClasses.length) {
                        toast.error("No coverage data — re-run the test first");
                        return;
                      }
                      const needsWork = coveredClasses.filter(
                        (coveredClass) => (coveredClass.coverage_pct ?? 0) < 100,
                      );
                      if (needsWork.length === 0) {
                        toast.success("All covered classes are at 100% — nothing to improve");
                        return;
                      }
                      const buildPrompt = (coveredClass: CoveredClass) => {
                        const uncoveredLines = (coveredClass.uncovered_lines || []).slice(0, 20).join(", ") || "—";
                        return `Improve test coverage for ${coveredClass.name} (currently ${coveredClass.coverage_pct ?? 0}%). Uncovered lines: ${uncoveredLines}. Write additional test methods in ${testClassName} to cover these lines.`;
                      };
                      const buildDisplay = (coveredClass: CoveredClass) =>
                        `Improve coverage for ${coveredClass.name}`;
                      // Multi-agent has no clarification feed (no addFeedItem): when
                      // several classes need work, target the lowest-coverage one.
                      const target = [...needsWork].sort(
                        (a, b) => (a.coverage_pct ?? 0) - (b.coverage_pct ?? 0),
                      )[0];
                      onSendMessage(buildPrompt(target), buildDisplay(target));
                    }}
                    className="text-[11px] font-medium text-ma-info-text hover:underline hover:underline"
                  >
                    Improve
                  </button>
                </div>
                {isExpanded && coveredClasses.length > 0 && (
                  <div
                    className="mt-2 border-t border-border/40 pt-2"
                    onClick={(event) => event.stopPropagation()}
                  >
                    <div className="overflow-x-auto">
                    <table className="w-full text-[11px]">
                      <thead className="text-muted-foreground font-medium">
                        <tr>
                          <th className="text-left font-normal pb-1">Class</th>
                          <th className="text-right font-normal pb-1">%</th>
                          <th className="text-right font-normal pb-1">Cov</th>
                          <th className="text-right font-normal pb-1">Uncov</th>
                        </tr>
                      </thead>
                      <tbody>
                        {coveredClasses.map((coveredClass) => {
                          const percent = coveredClass.coverage_pct ?? 0;
                          const colorClass =
                            percent >= 75
                              ? "text-ma-ok-text"
                              : percent >= 50
                                ? "text-ma-warn-text"
                                : "text-ma-err-text";
                          return (
                            <tr key={coveredClass.name} className="border-t border-border/20">
                              <td className="py-1 truncate max-w-[140px]" title={coveredClass.name}>
                                {coveredClass.name}
                              </td>
                              <td className={`py-1 text-right ${colorClass}`}>
                                {coveredClass.coverage_pct ?? "—"}%
                              </td>
                              <td className="py-1 text-right text-muted-foreground">
                                {coveredClass.num_covered}
                              </td>
                              <td className="py-1 text-right text-muted-foreground">
                                {coveredClass.num_uncovered}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    </div>
                  </div>
                )}
                {isExpanded && coveredClasses.length === 0 && (
                  <div
                    className="mt-2 text-[11px] text-muted-foreground italic"
                    onClick={(event) => event.stopPropagation()}
                  >
                    No coverage data
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : aggregateRows.length === 0 ? (
        <div className="text-muted-foreground text-center py-2">
          No test data yet
        </div>
      ) : null}
    </PanelSection>
  );
}
